import React, {useCallback, useState, useEffect} from 'react'
import {Col, ButtonSecondary, ProgressButton, Row} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {omit} from 'ramda'
import {FormFooterContainer, BaseFormFooterContainer} from '../../../../ui/form/styles'
import {FooterRow, DiscardButtonLink} from './styles'
import NavigationHook from '../navigation-hook'
import {CustomFormCtaFooterProps} from './types'

type ButtonSecondaryProgressProps = Omit<
  React.ComponentProps<typeof ProgressButton>,
  'buttonComponent'
> & {
  customSubmitButton?: React.ElementType
}

const ButtonSecondaryProgress = ({customSubmitButton, ...props}: ButtonSecondaryProgressProps) => (
  <ProgressButton
    buttonComponent={customSubmitButton || ButtonSecondary}
    {...omit(['buttonComponent'], props)}
  />
)

const CustomFormCtaFooter: React.FC<CustomFormCtaFooterProps> = ({
  loading,
  submitLabel = '',
  canSubmit = true,
  onSubmit,
  discardChanges,
  hasUnsavedChanges,
  onDiscard,
  lockNavigation = true,
  formIsValid = true,
  hasNavLock = true,
  disableSubmit = false,
  disableDiscard = false,
  isAlwaysVisible = false,
  hasDiscard = true,
  discardLabel,
  hasSubmit = true,
  hasBackButton = false,
  canMoveBack = false,
  onBackButtonClick,
  onForwardButtonClick,
  canMoveForward = false,
  isFinalStep = false,
  hasError,
  customSubmitButton,
  dataTargetSuffix = '',
}) => {
  const {t} = useTranslation('charon')
  const [isContinuePending, setIsContinuePending] = useState(false)
  const [isBackPending, setIsBackPending] = useState(false)

  const SubmitButton = customSubmitButton || ButtonSecondary

  // Move to next or previous step
  useEffect(() => {
    if (!loading && isContinuePending && !hasError) {
      setIsContinuePending(false)
      onForwardButtonClick && onForwardButtonClick()
    } else if (!loading && isBackPending && !hasError) {
      onBackButtonClick && onBackButtonClick()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const handleContinueClick = useCallback(
    (e?: React.UIEvent) => {
      e && e.preventDefault()
      if (hasUnsavedChanges) {
        onSubmit()
        if (!isFinalStep) {
          setIsContinuePending(true)
        }
      } else if (canMoveForward) {
        onForwardButtonClick && onForwardButtonClick()
      }
    },
    [onSubmit, hasUnsavedChanges, onForwardButtonClick, canMoveForward, isFinalStep]
  )

  const handleBackButtonClick = useCallback(
    (e?: React.UIEvent) => {
      e && e.preventDefault()
      if (hasUnsavedChanges) {
        onSubmit()
        setIsBackPending(true)
      } else if (canMoveBack) {
        onBackButtonClick && onBackButtonClick()
      }
    },
    [onSubmit, hasUnsavedChanges, onBackButtonClick, canMoveBack]
  )

  const handleDiscardClick = useCallback(
    (e?: React.UIEvent) => {
      e && e.preventDefault()
      discardChanges && discardChanges()
      onDiscard && onDiscard()
    },
    [discardChanges, onDiscard]
  )

  return (
    <>
      <NavigationHook
        onConfirm={handleContinueClick}
        onCancel={handleDiscardClick}
        isFinalStep={isFinalStep}
        canSubmit={!!canSubmit && !!formIsValid}
        navigationLocked={!!lockNavigation && !!hasNavLock}
        loading={!!loading}
        hasError={hasError}
      />
      {hasUnsavedChanges || isAlwaysVisible ? (
        <FormFooterContainer>
          <FooterRow hasDiscard={hasDiscard} hasSubmit={hasSubmit} justifyContent="end">
            <Col col={8} md={6}>
              <Row>
                {hasDiscard && (
                  <Col auto>
                    <DiscardButtonLink
                      transparent
                      type="button"
                      data-target="discard"
                      onClick={handleDiscardClick}
                      active={!disableDiscard && !loading && hasUnsavedChanges}
                    >
                      {discardLabel || t(`sub-section-footer.cta-reset`)}
                    </DiscardButtonLink>
                  </Col>
                )}
                {hasBackButton && (
                  <Col auto>
                    <ButtonSecondary
                      data-target="back-nav"
                      active={canMoveBack && !loading}
                      inverse
                      onClick={handleBackButtonClick}
                    >
                      {t('sub-section-footer.cta-back')}
                    </ButtonSecondary>
                  </Col>
                )}
              </Row>
            </Col>
            <Col col={4} md={6}>
              {!!hasSubmit && (
                <Row justifyContent="end">
                  <Col auto>
                    {typeof loading === 'undefined' ? (
                      <SubmitButton
                        data-target={
                          ((hasUnsavedChanges && formIsValid) || isFinalStep ? 'submit' : 'next') +
                          (dataTargetSuffix ? `-${dataTargetSuffix}` : '')
                        }
                        active={canSubmit}
                        onClick={handleContinueClick}
                      >
                        {submitLabel || (isFinalStep ? 'Save' : 'Next')}
                      </SubmitButton>
                    ) : (
                      <ButtonSecondaryProgress
                        data-target={
                          ((hasUnsavedChanges && formIsValid) || isFinalStep ? 'submit' : 'next') +
                          (dataTargetSuffix ? `-${dataTargetSuffix}` : '')
                        }
                        customSubmitButton={customSubmitButton}
                        active={canSubmit && !disableSubmit}
                        onClick={handleContinueClick}
                        loading={loading}
                      >
                        {submitLabel || (isFinalStep ? 'Save' : 'Next')}
                      </ButtonSecondaryProgress>
                    )}
                  </Col>
                </Row>
              )}
            </Col>
          </FooterRow>
        </FormFooterContainer>
      ) : (
        <BaseFormFooterContainer />
      )}
    </>
  )
}

export default CustomFormCtaFooter
